import Nango from '@nangohq/frontend';

import { env } from '@/lib/env';

import { IntegrationAuthParams } from '../../components/flow-canvas/components/side-menu/side-menu.types';
import { ERROR_MESSAGES } from './registry/constants';
import type { Integration, IntegrationAppAuthentications } from './types';
import { CustomIntegrationProvider, IntegrationAuthApp, NangoIntegrationProvider } from './types';

export const createNangoInstance = async (nangoPublicKey: string) =>
  new Nango({
    host: env.NEXT_PUBLIC_NANGO_HOST,
    publicKey: nangoPublicKey,
  });

export const initConnection = async (
  integration: IntegrationAuthApp,
  connectionId: string,
  nangoPublicKey: string,
  authParams: IntegrationAuthParams,
) => {
  const nango = await createNangoInstance(nangoPublicKey);
  switch (integration) {
    case IntegrationAuthApp.freshdeskV1:
      if (!authParams?.subdomain) {
        throw new Error(ERROR_MESSAGES.SUBDOMAIN_NOT_FOUND.description);
      }
      return await nango.auth(IntegrationAuthApp.freshdeskV1, connectionId, {
        params: {
          subdomain: authParams.subdomain,
        },
        credentials: {
          username: env.NEXT_PUBLIC_FRESHDESK_API_KEY,
          password: 'x', // freshdesk asks you to leave "x" here
        },
      });

    case IntegrationAuthApp.freshserviceEnpal:
      if (!authParams?.subdomain) {
        throw new Error(ERROR_MESSAGES.SUBDOMAIN_NOT_FOUND.description);
      }
      return await nango.auth(IntegrationAuthApp.freshserviceEnpal, connectionId, {
        params: {
          subdomain: authParams.subdomain,
        },
        credentials: {
          username: env.NEXT_PUBLIC_FRESHDESK_ENPAL_API_KEY,
          password: 'x', // freshdesk asks you to leave "x" here
        },
      });

    case IntegrationAuthApp.microsoftTeams:
    case IntegrationAuthApp.microsoftTeamsV2:
      return await nango.auth(integration, connectionId, {
        params: { ...authParams },
        authorization_params: {
          prompt: 'select_account', // fixes users stuck on outlook admin consent (PD-1285)
        },
      });

    case IntegrationAuthApp.gmailV1:
    case IntegrationAuthApp.gsheetsV1:
      return await nango.auth(integration, connectionId, {
        params: { ...authParams },
        authorization_params: {
          prompt: 'select_account consent', // we need both for refresh tokens to work (PD-1351)
        },
      });

    case IntegrationAuthApp.gw:
      return await nango.auth(IntegrationAuthApp.gw, connectionId, {
        params: {
          ...authParams,
          ...(authParams.clientScopes && { oauth_scopes: authParams.clientScopes }),
        },
        credentials: {
          client_id: authParams.clientId ?? '',
          client_secret: authParams.clientSecret ?? '',
        },
      });

    case IntegrationAuthApp.microsoftEntra:
      return new Promise((resolve, reject) =>
        nango.openConnectUI({
          sessionToken: authParams.sessionToken,
          onEvent: (event) => {
            if (event.type === 'connect') {
              return resolve({
                connectionId: event.payload.connectionId,
                providerConfigKey: event.payload.providerConfigKey,
              });
            }
            if (event.type === 'close') reject(event);
          },
        }),
      ) as Promise<{ connectionId: string; providerConfigKey: string }>;

    default:
      return await nango.auth(integration, connectionId, {
        params: { ...authParams },
      });
  }
};

export const integrations: Record<IntegrationAuthApp, Integration> = {
  [IntegrationAuthApp.gmailV1]: {
    id: IntegrationAuthApp.gmailV1,
    provider: NangoIntegrationProvider.GOOGLE,
    title: 'Gmail',
    icon: 'gmail',
    triggers: [
      {
        id: '2bf6e131-f76c-4746-8963-548206d2a2d9',
        description: 'new email is received',
      },
    ],
    targets: [
      {
        id: '7bf85c9c-a09c-4f88-bfb6-016a1dbc5bae',
        description: 'label email',
        compatibleWith: ['2bf6e131-f76c-4746-8963-548206d2a2d9'],
      },
    ],
  },
  [IntegrationAuthApp.gsheetsV1]: {
    id: IntegrationAuthApp.gsheetsV1,
    provider: NangoIntegrationProvider.GSHEETS,
    title: 'Google Sheets',
    icon: 'gsheets',
    triggers: [],
    targets: [],
  },
  [IntegrationAuthApp.zendeskV1]: {
    id: IntegrationAuthApp.zendeskV1,
    provider: NangoIntegrationProvider.ZENDESK,
    title: 'Zendesk',
    icon: 'zendesk',
    triggers: [],
    targets: [],
  },
  [IntegrationAuthApp.frontV1]: {
    id: IntegrationAuthApp.frontV1,
    provider: NangoIntegrationProvider.FRONT,
    title: 'Front',
    icon: 'front',
    triggers: [],
    targets: [],
  },
  [IntegrationAuthApp.microsoftTeams]: {
    id: IntegrationAuthApp.microsoftTeams,
    provider: NangoIntegrationProvider.MICROSOFT_TEAMS,
    title: 'Outlook V1',
    icon: 'outlook',
    triggers: [],
    targets: [],
  },
  [IntegrationAuthApp.microsoftTeamsV2]: {
    id: IntegrationAuthApp.microsoftTeamsV2,
    provider: NangoIntegrationProvider.MICROSOFT_TEAMS,
    title: 'Outlook',
    icon: 'outlook',
    triggers: [],
    targets: [],
  },

  [IntegrationAuthApp.slack]: {
    id: IntegrationAuthApp.slack,
    provider: NangoIntegrationProvider.SLACK,
    title: 'Slack',
    icon: 'slack',

    triggers: [],
    targets: [],
  },
  [IntegrationAuthApp.hubspotV1]: {
    id: IntegrationAuthApp.hubspotV1,
    provider: NangoIntegrationProvider.HUBSPOT,
    title: 'Hubspot',
    icon: 'hubspotV1',
    triggers: [],
    targets: [],
  },
  [IntegrationAuthApp.network]: {
    id: IntegrationAuthApp.network,
    provider: CustomIntegrationProvider.NETWORK,
    title: 'HTTPS',
    icon: 'network',
    triggers: [],
    targets: [],
  },
  [IntegrationAuthApp.webhook]: {
    id: IntegrationAuthApp.webhook,
    provider: CustomIntegrationProvider.WEBHOOK,
    title: 'Webhook',
    icon: 'network',
    triggers: [],
    targets: [],
  },
  [IntegrationAuthApp.customCode]: {
    id: IntegrationAuthApp.customCode,
    provider: CustomIntegrationProvider.CUSTOM_CODE,
    title: 'Custom Code',
    icon: 'customCode',
    triggers: [],
    targets: [],
  },
  [IntegrationAuthApp.freshdeskV1]: {
    id: IntegrationAuthApp.freshdeskV1,
    provider: NangoIntegrationProvider.FRESHDESK,
    title: 'Freshdesk',
    icon: 'freshdesk',
    triggers: [],
    targets: [],
  },
  [IntegrationAuthApp.freshserviceEnpal]: {
    id: IntegrationAuthApp.freshserviceEnpal,
    provider: NangoIntegrationProvider.FRESHDESK,
    title: 'Freshdesk',
    icon: 'freshdesk',
    triggers: [],
    targets: [],
  },
  [IntegrationAuthApp.development]: {
    id: IntegrationAuthApp.development,
    provider: CustomIntegrationProvider.DEVELOPMENT,
    title: 'Development',
    icon: 'development',
    triggers: [],
    targets: [],
  },
  [IntegrationAuthApp.gw]: {
    id: IntegrationAuthApp.gw,
    provider: NangoIntegrationProvider.GW,
    title: 'Gebrüder Weiss',
    icon: 'gw',
    triggers: [],
    targets: [],
  },
  [IntegrationAuthApp.microsoftEntra]: {
    id: IntegrationAuthApp.microsoftEntra,
    provider: NangoIntegrationProvider.MICROSOFT_OAUTH2_CC,
    title: 'Microsoft Entra',
    icon: 'microsoftEntra',
    triggers: [],
    targets: [],
  },
};

export const workspaceConnectionsByIntegration = (connections: IntegrationAppAuthentications[]) => {
  return connections.reduce<Record<IntegrationAuthApp, Array<IntegrationAppAuthentications>>>(
    (acc, connection) => {
      acc[connection.integrationAuthApp].push(connection);
      return acc;
    },
    {
      [IntegrationAuthApp.gmailV1]: [],
      [IntegrationAuthApp.gsheetsV1]: [],
      [IntegrationAuthApp.zendeskV1]: [],
      [IntegrationAuthApp.frontV1]: [],
      [IntegrationAuthApp.microsoftTeams]: [],
      [IntegrationAuthApp.microsoftTeamsV2]: [],
      [IntegrationAuthApp.slack]: [],
      [IntegrationAuthApp.hubspotV1]: [],
      [IntegrationAuthApp.network]: [],
      [IntegrationAuthApp.webhook]: [],
      [IntegrationAuthApp.customCode]: [],
      [IntegrationAuthApp.freshdeskV1]: [],
      [IntegrationAuthApp.freshserviceEnpal]: [],
      [IntegrationAuthApp.development]: [],
      [IntegrationAuthApp.gw]: [],
      [IntegrationAuthApp.microsoftEntra]: [],
    },
  );
};
